import { FieldProps } from "./field";
import { validate } from "../validation";
import { Dropdown, DropdownOption } from "./dropdown";
import { CountryCode, getCountryCallingCode } from "libphonenumber-js/min";
import {
  COUNTRIES_AS_DROPDOWN_OPTIONS,
  useOnCardCountryChange,
} from "./field-country";
import parsePhoneNumberFromString, {
  getExampleNumber,
  PhoneNumber,
} from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import { useSession } from "./session-provider";
import { formFieldName } from "../utils";
import { InternalInputValidateEvent } from "../private-event-types";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "preact/hooks";
import { FunctionComponent, TargetedEvent } from "preact";

export const PhoneNumberField: FunctionComponent<FieldProps> = (props) => {
  const { field, onChange, onError } = props;
  const id = formFieldName(field);

  const session = useSession();

  const hiddenFieldRef = useRef<HTMLInputElement>(null);
  const [isTouched, setIsTouched] = useState(false);

  const [countryCode, setCountryCode] = useState(session.country);
  const countryCodeIndex = useMemo(() => {
    const index = COUNTRIES_WITH_DIAL_CODES_AS_DROPDOWN_OPTIONS.findIndex(
      (r) => r.value === countryCode,
    );
    if (index === -1) return 0;
    return index;
  }, [countryCode]);
  const country =
    COUNTRIES_WITH_DIAL_CODES_AS_DROPDOWN_OPTIONS[countryCodeIndex];

  const [localNumber, setLocalNumber] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const formatPhoneNumber = useCallback(
    (country: DropdownOptionWithDial, localNumber: string) => {
      const phoneNumber = sanitizePhoneNumber(country, localNumber);
      if (phoneNumber) {
        // use parsed format if parsing was successful
        return phoneNumber.number;
      } else {
        // else just concat the dial code and local number
        return `+${country.dial}${localNumber}`;
      }
    },
    [],
  );

  const updateValidity = useCallback(
    (nextCountry: DropdownOptionWithDial, localNumber: string) => {
      const phoneNumberString = formatPhoneNumber(nextCountry, localNumber);
      const errorCode =
        validate(field, localNumber.length ? phoneNumberString : "") ?? null;
      if (onError) onError(id, errorCode);
    },
    [field, formatPhoneNumber, id, onError],
  );

  const updateHiddenField = useCallback(
    (country: DropdownOptionWithDial, localNumber: string) => {
      if (hiddenFieldRef.current) {
        hiddenFieldRef.current.value = formatPhoneNumber(country, localNumber);
      }
    },
    [formatPhoneNumber],
  );

  useEffect(() => {
    const input = hiddenFieldRef.current;
    if (!input) return;
    const listener = (e: Event) => {
      const value = (e as CustomEvent).detail.value;
      updateValidity(country, value);
    };
    input.addEventListener(InternalInputValidateEvent.type, listener);
    return () => {
      input.removeEventListener(InternalInputValidateEvent.type, listener);
    };
  }, [id, updateValidity, country]);

  function handleLocalChange(event: TargetedEvent<HTMLInputElement>): void {
    const nextLocal = (event.target as HTMLInputElement).value;
    setLocalNumber(nextLocal);
    updateHiddenField(country, nextLocal);
    setIsTouched(true);
    updateValidity(country, nextLocal);
    onChange(); // keep parity with other fields
  }

  function handleCountryChange(option: DropdownOption): void {
    const nextCountry = option as DropdownOptionWithDial;
    setCountryCode(nextCountry.value as string);
    updateHiddenField(nextCountry, localNumber);
    if (isTouched) updateValidity(nextCountry, localNumber);
    onChange(); // keep parity with other fields
  }

  // when the user inputs a card number, update the phone number field to match
  useOnCardCountryChange((newCountry: CountryCode) => {
    const newOption = COUNTRIES_WITH_DIAL_CODES_AS_DROPDOWN_OPTIONS.find(
      (option) => option.value === newCountry,
    );
    if (newOption && newOption.value !== countryCode) {
      handleCountryChange(newOption);
    }
  });

  function getExampleLocalNumber() {
    return (
      getExampleNumber(country.value as CountryCode, examples)
        ?.formatInternational()
        ?.replace(
          `+${getCountryCallingCode(country.value as CountryCode)} `,
          "",
        ) || ""
    );
  }

  function formatForUser() {
    const phoneNumber = sanitizePhoneNumber(country, localNumber);
    if (phoneNumber) {
      const international = phoneNumber.formatInternational();
      // remove country dial code from displayed local number
      setLocalNumber(
        international.replace(
          `+${getCountryCallingCode(country.value as CountryCode)} `,
          "",
        ),
      );
    }
  }

  function handleBlur(): void {
    setIsTouched(true);
    formatForUser();
    updateValidity(country, localNumber);
  }

  return (
    <div className={`xendit-input-phone`}>
      <div className="xendit-combobox">
        <Dropdown
          options={COUNTRIES_WITH_DIAL_CODES_AS_DROPDOWN_OPTIONS}
          selectedIndex={countryCodeIndex}
          onChange={handleCountryChange}
        />
        <input
          id={id}
          ref={inputRef}
          type="tel"
          inputMode="tel"
          placeholder={getExampleLocalNumber()}
          className="xendit-text-14 xendit-phone-number-input"
          onBlur={handleBlur}
          onChange={handleLocalChange}
          value={localNumber}
          autoComplete="tel"
        />
      </div>

      {/* Hidden canonical value (useful for non-JS form posts) */}
      <input type="hidden" name={id} ref={hiddenFieldRef} />
    </div>
  );
};

type DropdownOptionWithDial = DropdownOption & { dial: string };
const COUNTRIES_WITH_DIAL_CODES_AS_DROPDOWN_OPTIONS =
  COUNTRIES_AS_DROPDOWN_OPTIONS.map<DropdownOptionWithDial | null>(
    (country) => {
      const dial = getCountryCallingCode(country.value as CountryCode);
      if (!dial) return null;
      return {
        ...country,
        shortTitle: `+${dial}`,
        title: `${country.title} (+${dial})`,
        dial,
      };
    },
  ).filter((country): country is DropdownOptionWithDial => {
    return Boolean(country);
  });

const sanitizePhoneNumber = (
  country: DropdownOptionWithDial,
  phoneNumber: string,
): PhoneNumber | null => {
  const parsed = parsePhoneNumberFromString(
    phoneNumber,
    country.value as CountryCode,
  );
  if (parsed && parsed.isPossible()) return parsed;

  return null;
};
